import { useMemo } from "react";
import { useCreditSummary } from "@/hooks/useCredits";
import { usePermissions } from "@/hooks/usePermissions";

/** Decimal string → a non-negative number. Anything unparseable is 0, never NaN on a money screen. */
function toPositive(value: string | undefined): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * The credit balance the campaign-builder checkout may apply against the total.
 *
 * Credits are held per group, so this resolves one. `walletGroupId` is the wallet a multi-group
 * client (Pro Plus or Enterprise) chose; without one it falls back to the caller's own placement,
 * which is every other plan.
 *
 * What it reports is what the campaign may be funded with, NOT the wallet balance:
 *
 *   available = credits that outlive the campaign − headroom the group's live campaigns hold
 *
 * The checkout is
 * denominated in USD while the balance is denominated in credits: **1 credit is treated as 1 USD**
 * (confirmed with the product owner, 2026-08-12). The summary payload also carries
 * `creditsPerUsd` — if that ratio ever stops being 1, convert here rather than at the slider, so
 * the cost breakdown keeps receiving dollars.
 *
 * Reading the summary requires `view CREDIT`. Without it the query is left disabled (groupId
 * passed as null) and the balance reports 0, which hides the credits block entirely instead of
 * surfacing a 403.
 */
export function useCampaignCredits(
  walletGroupId?: number | null,
  /** When this campaign closes, as an ISO instant. Decides which credits are still alive for it. */
  until?: string | null,
): {
  balance: number;
  /** What the group HOLDS, before its live campaigns' headroom comes off. */
  walletBalance: number;
  /** The headroom the group's live campaigns hold — what makes a full wallet unusable here. */
  heldByOthers: number;
  isLoading: boolean;
} {
  const { groupId: placement, can, isLoaded } = usePermissions();
  const groupId = walletGroupId ?? placement;
  const effectiveGroupId = can("view", "CREDIT") ? groupId : null;
  const { data, isLoading } = useCreditSummary(effectiveGroupId, until);

  // AVAILABLE, not balance. The wallet's balance is the wrong number to offer a new campaign twice
  // over: some of it is headroom another live campaign is already holding, and some of it expires
  // before this campaign closes and so cannot pay for a redemption after it is gone. Decimal
  // string, numeric on the server.
  const balance = useMemo(() => {
    const parsed = Number(data?.available ?? 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [data?.available]);

  // The wallet's own figures, so the checkout can EXPLAIN a zero rather than hiding the row. Both
  // stay 0 when the caller lacks `view CREDIT`, because the query is disabled and there is no
  // payload — which is what keeps the explanation off a screen whose reader may not see credits.
  const walletBalance = useMemo(() => toPositive(data?.availableDetail?.balance), [data]);
  const heldByOthers = useMemo(() => toPositive(data?.availableDetail?.budgeted), [data]);

  return {
    balance,
    walletBalance,
    heldByOthers,
    isLoading: !isLoaded || (effectiveGroupId != null && isLoading),
  };
}
